// src/pages/VideoSettingsPage.tsx
/**
 * VideoSettingsPage
 * Settings page for video template preferences
 *
 * Feature: Video Settings
 */

import { memo, useState, useCallback } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DefaultsTab } from '@/components/property/video-settings/DefaultsTab';
import { ManageTemplatesTab } from '@/components/property/video-settings/ManageTemplatesTab';
import { MyTemplatesTab } from '@/components/property/video-settings/MyTemplatesTab';
import type { TemplateCategory, IUserTemplateFormData, TemplateId } from '@/@types/videoSettings';
import type { Id } from '../../convex/_generated/dataModel';

function VideoSettingsPage() {
  const [activeTab, setActiveTab] = useState('defaults');

  // Queries
  const preferences = useQuery(api.property.userTemplatePreferences.get);
  const systemTemplates = useQuery(api.property.promptTemplates.list);
  const userTemplates = useQuery(api.property.userPromptTemplates.list);

  // Mutations
  const setDefault = useMutation(api.property.userTemplatePreferences.setDefault);
  const excludeTemplate = useMutation(api.property.userTemplatePreferences.excludeTemplate);
  const includeTemplate = useMutation(api.property.userTemplatePreferences.includeTemplate);
  const createUserTemplate = useMutation(api.property.userPromptTemplates.create);
  const updateUserTemplate = useMutation(api.property.userPromptTemplates.update);
  const deleteUserTemplate = useMutation(api.property.userPromptTemplates.remove);

  // Handlers
  const handleSetDefault = useCallback(
    (category: TemplateCategory, templateId: TemplateId | null) => {
      setDefault({ category, templateId });
    },
    [setDefault]
  );

  const handleToggleExclusion = useCallback(
    (templateId: Id<'promptTemplates'>, exclude: boolean) => {
      if (exclude) {
        excludeTemplate({ templateId });
      } else {
        includeTemplate({ templateId });
      }
    },
    [excludeTemplate, includeTemplate]
  );

  const handleCreateTemplate = useCallback(
    async (data: IUserTemplateFormData) => {
      await createUserTemplate(data);
    },
    [createUserTemplate]
  );

  const handleUpdateTemplate = useCallback(
    async (templateId: Id<'userPromptTemplates'>, data: IUserTemplateFormData) => {
      await updateUserTemplate({ templateId, ...data });
    },
    [updateUserTemplate]
  );

  const handleDeleteTemplate = useCallback(
    async (templateId: Id<'userPromptTemplates'>) => {
      await deleteUserTemplate({ templateId });
    },
    [deleteUserTemplate]
  );

  const isLoading =
    preferences === undefined || systemTemplates === undefined || userTemplates === undefined;

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Video Settings</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          Configure default templates and manage your custom video styles
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="defaults">Defaults</TabsTrigger>
          <TabsTrigger value="manage">Manage Templates</TabsTrigger>
          <TabsTrigger value="my-templates">My Templates</TabsTrigger>
        </TabsList>

        <TabsContent value="defaults" className="mt-6">
          <DefaultsTab
            preferences={preferences ?? null}
            systemTemplates={systemTemplates ?? []}
            userTemplates={userTemplates ?? []}
            onSetDefault={handleSetDefault}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="manage" className="mt-6">
          <ManageTemplatesTab
            systemTemplates={systemTemplates ?? []}
            excludedIds={preferences?.excludedTemplateIds ?? []}
            onToggleExclusion={handleToggleExclusion}
            isLoading={isLoading}
          />
        </TabsContent>

        <TabsContent value="my-templates" className="mt-6">
          <MyTemplatesTab
            templates={userTemplates ?? []}
            onCreate={handleCreateTemplate}
            onUpdate={handleUpdateTemplate}
            onDelete={handleDeleteTemplate}
            isLoading={isLoading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const VideoSettingsPageMemo = memo(VideoSettingsPage);
